import { useState } from "react";
import { Loader2, ClipboardList } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const IntakeFormFill = ({ bookingId, registrationId, formId, questions, title, onDone, onSkip }) => {
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    const missing = questions.filter((q) => q.required && !(answers[q.id] || "").trim());
    if (missing.length) { setError(`Please answer: ${missing.map((q) => q.label).join(", ")}`); return; }
    setSaving(true); setError("");
    try {
      await api.post("/intake/submit", {
        booking_id: bookingId || "", registration_id: registrationId || "", form_id: formId || "",
        answers: questions.map((q) => ({ id: q.id, label: q.label, answer: answers[q.id] || "" })),
      });
      toast.success("Intake form saved — thank you!");
      onDone?.();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || "Couldn't save your answers."); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="intake-form-fill">
      <p className="eyw-label mb-1 flex items-center gap-1.5"><ClipboardList size={13} /> Intake form</p>
      <h3 className="font-serif text-xl text-[#1A242B]">{title || "A few quick questions before your session"}</h3>
      <p className="text-sm text-[#4A5D6B] mt-1">Your answers go privately to your practitioner so they can prepare for you.</p>
      <div className="space-y-3.5 mt-4">
        {questions.map((q) => (
          <div key={q.id}>
            <label className="text-sm text-[#1A242B] block mb-1">{q.label}{q.required && <span className="text-[#D98E73]"> *</span>}</label>
            {q.type === "textarea" ? (
              <textarea rows={3} value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} data-testid={`intake-answer-${q.id}`} className="eyw-input w-full resize-none" />
            ) : q.type === "select" ? (
              <select value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} data-testid={`intake-answer-${q.id}`} className="eyw-input w-full">
                <option value="">Select…</option>
                {(q.options || []).filter(Boolean).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : q.type === "checkboxes" ? (
              <div className="flex flex-wrap gap-2">
                {(q.options || []).filter(Boolean).map((o) => {
                  const cur = (answers[q.id] || "").split(", ").filter(Boolean);
                  const on = cur.includes(o);
                  return (
                    <button key={o} type="button" data-testid={`intake-answer-${q.id}-${o.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setAnswers({ ...answers, [q.id]: (on ? cur.filter((x) => x !== o) : [...cur, o]).join(", ") })}
                      className={`px-4 py-2 rounded-full border text-sm transition-colors ${on ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]/50"}`}>{o}</button>
                  );
                })}
              </div>
            ) : q.type === "scale" ? (
              <div className="flex gap-2" data-testid={`intake-answer-${q.id}`}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setAnswers({ ...answers, [q.id]: String(n) })} data-testid={`intake-answer-${q.id}-${n}`}
                    className={`h-10 w-10 rounded-full border text-sm font-medium transition-colors ${answers[q.id] === String(n) ? "bg-[#D98E73] text-white border-[#D98E73]" : "border-[#E5E0D8] text-[#4A5D6B] hover:border-[#D98E73]/60"}`}>{n}</button>
                ))}
              </div>
            ) : q.type === "yes_no" ? (
              <div className="flex gap-2">
                {["Yes", "No"].map((o) => (
                  <button key={o} type="button" onClick={() => setAnswers({ ...answers, [q.id]: o })} data-testid={`intake-answer-${q.id}-${o.toLowerCase()}`}
                    className={`px-5 py-2 rounded-full border text-sm transition-colors ${answers[q.id] === o ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]/50"}`}>{o}</button>
                ))}
              </div>
            ) : (
              <input value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} data-testid={`intake-answer-${q.id}`} className="eyw-input w-full" />
            )}
          </div>
        ))}
      </div>
      {error && <p className="text-sm text-red-600 mt-3" data-testid="intake-fill-error">{error}</p>}
      <div className="flex items-center gap-3 mt-5">
        <button onClick={submit} disabled={saving} data-testid="intake-fill-submit" className="eyw-btn flex-1 justify-center disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={16} />} Save my answers</button>
        {onSkip && <button onClick={onSkip} data-testid="intake-fill-skip" className="text-sm text-[#8FA89B] hover:text-[#4A5D6B]">Do this later</button>}
      </div>
    </div>
  );
};

export default IntakeFormFill;
